import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { isTelegram, isStandalone } from '../detector';

const ThemeContext = createContext(null);

// Default theme values matching Telegram's light theme
const lightTheme = {
  bgColor: '#ffffff',
  textColor: '#000000',
  hintColor: '#999999',
  linkColor: '#2481cc',
  buttonColor: '#3390ec',
  buttonTextColor: '#ffffff',
  secondaryBgColor: '#f4f4f5',
  sectionSeparatorColor: '#e5e5e5',
  headerBgColor: '#ffffff',
  accentTextColor: '#3390ec',
  sectionBgColor: '#ffffff',
  sectionHeaderTextColor: '#6d6d72',
  subtitleTextColor: '#999999',
  destructiveTextColor: '#ff3b30',
  bottomBarBgColor: '#f8f8f8'
};

// Default theme values matching Telegram's dark theme
const darkTheme = {
  bgColor: '#212121',
  textColor: '#ffffff',
  hintColor: '#aaaaaa',
  linkColor: '#8ab4f8',
  buttonColor: '#8ab4f8',
  buttonTextColor: '#000000',
  secondaryBgColor: '#181818',
  sectionSeparatorColor: '#333333',
  headerBgColor: '#212121',
  accentTextColor: '#8ab4f8',
  sectionBgColor: '#212121',
  sectionHeaderTextColor: '#8d8e93',
  subtitleTextColor: '#8d8e93',
  destructiveTextColor: '#ff453a',
  bottomBarBgColor: '#1c1c1e'
};

const THEME_STORAGE_KEY = 'footy_theme';

/**
 * ThemeProvider manages theming for both Telegram and standalone modes
 * In Telegram mode, it uses Telegram's theme
 * In standalone mode, it provides light/dark theme toggle
 */
export function ThemeProvider({ children }) {
  const [colorScheme, setColorScheme] = useState('light');
  const [theme, setTheme] = useState(lightTheme);

  // Initialize theme
  useEffect(() => {
    if (isTelegram) {
      initTelegramTheme();
    } else {
      initStandaloneTheme();
    }
  }, []);

  // Listen for Telegram theme changes
  useEffect(() => {
    if (!isTelegram) return;

    const tg = window.Telegram.WebApp;
    const handleThemeChange = () => {
      setColorScheme(tg.colorScheme || 'light');
    };

    tg.onEvent('themeChanged', handleThemeChange);
    return () => tg.offEvent('themeChanged', handleThemeChange);
  }, []);

  // Listen for system theme changes (standalone only)
  useEffect(() => {
    if (!isStandalone) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      // Only auto-switch if user hasn't set a preference
      const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
      if (!savedTheme) {
        const newScheme = e.matches ? 'dark' : 'light';
        setColorScheme(newScheme);
        setTheme(e.matches ? darkTheme : lightTheme);
        applyThemeVariables(e.matches ? darkTheme : lightTheme);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const initTelegramTheme = () => {
    const tg = window.Telegram.WebApp;
    setColorScheme(tg.colorScheme || 'light');
    // Telegram already sets CSS variables, so we just track the scheme
  };

  const initStandaloneTheme = () => {
    // Check for saved preference
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);

    // Check system preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;

    // Use saved preference, or fall back to system preference
    const scheme = savedTheme || (prefersDark ? 'dark' : 'light');

    setColorScheme(scheme);
    const selectedTheme = scheme === 'dark' ? darkTheme : lightTheme;
    setTheme(selectedTheme);
    applyThemeVariables(selectedTheme);
  };

  const toggleTheme = useCallback(() => {
    if (!isStandalone) return; // Can't toggle in Telegram

    const newScheme = colorScheme === 'dark' ? 'light' : 'dark';
    const newTheme = newScheme === 'dark' ? darkTheme : lightTheme;

    setColorScheme(newScheme);
    setTheme(newTheme);
    localStorage.setItem(THEME_STORAGE_KEY, newScheme);
    applyThemeVariables(newTheme);
  }, [colorScheme]);

  const setThemeMode = useCallback((mode) => {
    if (!isStandalone) return;
    if (mode !== 'light' && mode !== 'dark') return;

    const newTheme = mode === 'dark' ? darkTheme : lightTheme;

    setColorScheme(mode);
    setTheme(newTheme);
    localStorage.setItem(THEME_STORAGE_KEY, mode);
    applyThemeVariables(newTheme);
  }, []);

  const value = useMemo(() => ({
    colorScheme,
    theme,
    isDark: colorScheme === 'dark',
    isLight: colorScheme === 'light',
    toggleTheme,
    setThemeMode,
    canToggle: isStandalone
  }), [colorScheme, theme, toggleTheme, setThemeMode]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Apply theme CSS variables to document root
 */
function applyThemeVariables(theme) {
  const root = document.documentElement;
  const isDark = theme === darkTheme;

  // Toggle Tailwind dark class
  if (isDark) {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }

  // Set all Telegram-compatible CSS variables
  root.style.setProperty('--tg-theme-bg-color', theme.bgColor);
  root.style.setProperty('--tg-theme-text-color', theme.textColor);
  root.style.setProperty('--tg-theme-hint-color', theme.hintColor);
  root.style.setProperty('--tg-theme-link-color', theme.linkColor);
  root.style.setProperty('--tg-theme-button-color', theme.buttonColor);
  root.style.setProperty('--tg-theme-button-text-color', theme.buttonTextColor);
  root.style.setProperty('--tg-theme-secondary-bg-color', theme.secondaryBgColor);
  root.style.setProperty('--tg-theme-section-separator-color', theme.sectionSeparatorColor);
  root.style.setProperty('--tg-theme-header-bg-color', theme.headerBgColor);
  root.style.setProperty('--tg-theme-accent-text-color', theme.accentTextColor);
  root.style.setProperty('--tg-theme-section-bg-color', theme.sectionBgColor);
  root.style.setProperty('--tg-theme-section-header-text-color', theme.sectionHeaderTextColor);
  root.style.setProperty('--tg-theme-subtitle-text-color', theme.subtitleTextColor);
  root.style.setProperty('--tg-theme-destructive-text-color', theme.destructiveTextColor);
  root.style.setProperty('--tg-theme-bottom-bar-bg-color', theme.bottomBarBgColor);

  // Also set color-scheme for native elements
  root.style.colorScheme = isDark ? 'dark' : 'light';
}

/**
 * Hook to access theme context
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
